import { onIdTokenChanged, User } from "firebase/auth";
import { GA_ACTION, GA_CATEGORIES } from "utils/constants";
import { auth } from "utils/firebase";
import { triggerGa } from "utils/gaUtils";
import { proxy, useSnapshot } from "valtio";

const ADMIN_TOKEN_KEY = "adminToken";
const FIREBASE_TOKEN_KEY = "firebaseToken";
const RESOLVE_TIMEOUT_MS = 30000;

type BackendLink = "idle" | "pending" | "linked";

type AuthState = {
  isReady: boolean;
  isLoggedIn: boolean;
  token: string | null;
  backendLink: BackendLink;
};

const noop = (): void => undefined;

const readAdminToken = () =>
  localStorage.getItem(ADMIN_TOKEN_KEY)?.replaceAll('"', "") || null;

const persistFirebaseToken = (token: string | null) =>
  token
    ? localStorage.setItem(FIREBASE_TOKEN_KEY, token)
    : localStorage.removeItem(FIREBASE_TOKEN_KEY);

const notifyNativeShell = (firebaseToken: string | null) => {
  const bridge = (window as any).ReactNativeWebView;
  if (!bridge?.postMessage) return;

  const payload = { firebaseToken, adminToken: readAdminToken() };
  bridge.postMessage(JSON.stringify({ type: "token", payload }));
};

const trackAuthEvent = (action: string, label = "", detail = "") => {
  try {
    triggerGa({
      category: GA_CATEGORIES.LOGIN,
      action,
      label,
      label2: detail,
    });
  } catch {
    noop();
  }
};

export const authStore = proxy<AuthState>({
  isReady: false,
  isLoggedIn: Boolean(readAdminToken()),
  token: readAdminToken(),
  backendLink: "idle",
});

export const useAuthStore = () => useSnapshot(authStore);

let resolveReady: () => void;
export const authReady = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

const applyFirebaseToken = (firebaseToken: string | null) => {
  authStore.token = firebaseToken ?? readAdminToken();
  authStore.isLoggedIn = Boolean(authStore.token);
  persistFirebaseToken(firebaseToken);
  notifyNativeShell(firebaseToken);
};

export const resetAuth = () => {
  authStore.backendLink = "idle";
  applyFirebaseToken(null);
};

export const beginBackendLink = () => {
  authStore.backendLink = "pending";
};

export const completeBackendLink = () => {
  authStore.backendLink = "linked";
};

export const failBackendLink = () => {
  if (authStore.backendLink === "pending") authStore.backendLink = "idle";
};

let pendingForceRefresh: Promise<string | null> | null = null;

const forceRefresh = (user: User) => {
  if (!pendingForceRefresh) {
    pendingForceRefresh = user
      .getIdToken(true)
      .then((token) => {
        applyFirebaseToken(token);
        trackAuthEvent(GA_ACTION.AUTH_FORCE_REFRESH, "success");
        return token as string | null;
      })
      .catch((error) => {
        trackAuthEvent(
          GA_ACTION.AUTH_FORCE_REFRESH,
          "fail",
          error?.code || "unknown"
        );
        return authStore.token;
      })
      .finally(() => {
        pendingForceRefresh = null;
      });
  }
  return pendingForceRefresh;
};

export const getAuthToken = async ({
  force = false,
}: { force?: boolean } = {}): Promise<string | null> => {
  const adminToken = readAdminToken();
  if (adminToken) return adminToken;

  await authReady;

  const user = auth.currentUser;
  if (!user) return authStore.token;
  if (force) return forceRefresh(user);

  try {
    return await user.getIdToken();
  } catch {
    return authStore.token;
  }
};

let hadFirebaseUser = false;

const onFirebaseSignedOut = (wasSignedIn: boolean) => {
  applyFirebaseToken(null);
  trackAuthEvent(
    GA_ACTION.AUTH_STATE_CHANGED,
    "signed_out",
    wasSignedIn ? "sign_out" : "first_resolve"
  );
};

const onFirebaseSignedIn = async (user: User, wasSignedIn: boolean) => {
  applyFirebaseToken(await user.getIdToken());
  trackAuthEvent(
    GA_ACTION.AUTH_STATE_CHANGED,
    "signed_in",
    wasSignedIn ? "token_refresh" : "session_established"
  );
};

const onTokenMintFailed = (error: any) => {
  authStore.isLoggedIn = true;
  trackAuthEvent(GA_ACTION.AUTH_TOKEN_REFRESH_FAILED, error?.code || "unknown");
};

let resolveTimeout: ReturnType<typeof setTimeout>;

const markResolved = () => {
  clearTimeout(resolveTimeout);
  authStore.isReady = true;
  resolveReady();
};

resolveTimeout = setTimeout(() => {
  if (authStore.isReady) return;
  markResolved();
  trackAuthEvent(GA_ACTION.AUTH_INIT_TIMEOUT);
}, RESOLVE_TIMEOUT_MS);

onIdTokenChanged(auth, async (user) => {
  const wasSignedIn = hadFirebaseUser;
  hadFirebaseUser = Boolean(user);
  try {
    if (user) await onFirebaseSignedIn(user, wasSignedIn);
    else onFirebaseSignedOut(wasSignedIn);
  } catch (error) {
    onTokenMintFailed(error);
  } finally {
    markResolved();
  }
});
